/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import SplineChart from '../../components/Charts/SplineChart';
import { useSelector } from 'react-redux';
import { Filters } from './Filters';
import PieChart from '../../components/Charts/PieChart';
import { fetchData, getPeriodRange } from '../../utils';
import Table from '../../components/Tables/Table';
import { periods as periodNew } from '../../constants';
import { toast } from 'react-toastify';
import {
  getAverageDeveloperCostFromSource,
  parseNumericInput,
  shouldFetchAverageDeveloperCostFromEndpoint
} from '../../utils/companyConfigurations';
import { sendRequest } from '../../utils/dateHelpers';

const getAverageDeveloperCost = userInfo => {
  const directCost = getAverageDeveloperCostFromSource(userInfo);

  if (directCost > 0) return directCost;

  return getAverageDeveloperCostFromSource(userInfo?.user);
};

const convertEffortToInvestment = (value, averageCost) => {
  const effortValue = parseNumericInput(value);
  return ((Number.isFinite(effortValue) ? effortValue : 0) / 60) * averageCost;
};

const convertNumericValues = (payload, averageCost) => {
  if (Array.isArray(payload)) {
    return payload.map(item => convertNumericValues(item, averageCost));
  }

  if (payload && typeof payload === 'object') {
    return Object.fromEntries(
      Object.entries(payload).map(([key, value]) => [
        key,
        convertNumericValues(value, averageCost)
      ])
    );
  }

  if (typeof payload === 'number') {
    return convertEffortToInvestment(payload, averageCost);
  }

  if (typeof payload === 'string') {
    const parsedValue = parseNumericInput(payload);

    if (Number.isFinite(parsedValue)) {
      return convertEffortToInvestment(parsedValue, averageCost);
    }
  }

  return payload;
};

const DashboardWidgetNew = ({
  category,
  pieChart = true,
  filtersShow = true,
  splineChart = true,
  chartVariant = 'spline',
  showCheckbox = true,
  dashboardFilters,
  title = {},
  serviceURL = '/dashboard/get-table-data',
  isHidden = [],
  isVisibleLangugageTags = false,
  children,
  defaultFilters = {},
  isCheckContributionHeatmap = false,
  operationKeys,
  forceClearMultiFilters = []
}) => {
  const [dashboardData, setDashboardData] = useState({});
  const [searchTable, setSearchTable] = useState('');
  const [checkboxFromTable, setCheckboxFromTable] = useState([]);
  const [periods, setPeriods] = useState({ chart1: periodNew.MONTH });
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [categoriesOrder, setCategoriesOrder] = useState([]);
  const [fallbackAverageDeveloperCost, setFallbackAverageDeveloperCost] =
    useState(0);
  const { userInfo = {} } = useSelector(state => state);
  const roleBasedAverageDeveloperCost = getAverageDeveloperCost(userInfo);
  const averageDeveloperCost =
    roleBasedAverageDeveloperCost > 0
      ? roleBasedAverageDeveloperCost
      : fallbackAverageDeveloperCost;
  const hasClientSideInvestmentRate = averageDeveloperCost > 0;

  const categoryArray = useMemo(() => {
    if (category === 'total') {
      return {
        total: {
          label:
            filters.operation === ''
              ? 'Number of Commits'
              : 'Baseline Effort (Without AI)'
        }
      };
    }

    const configuredCategories = userInfo?.dashboardData?.[category];
    if (category !== 'ai_adoption' || !configuredCategories) {
      return configuredCategories;
    }

    const isInvestment = filters.operation === 'investment';
    return {
      ...configuredCategories,
      ai: {
        ...configuredCategories.ai,
        label: isInvestment ? 'Estimated Cost Avoided' : 'Effort Saved by AI'
      },
      developer: {
        ...configuredCategories.developer,
        label: isInvestment
          ? 'Estimated Human Cost Required'
          : 'Human Effort Required'
      }
    };
  }, [category, filters.operation, userInfo?.dashboardData]);
  const [categoryArrayFilter, setCategoryArrayFilter] = useState(categoryArray);

  const {
    user: { companyId: companyUid }
  } = userInfo;

  const getData = async () => {
    try {
      setLoading(true);
      const groupPeriod = periods?.chart1 || 'day';

      const filtersNew = { ...filters };

      filtersNew.dateRange = getPeriodRange(
        filtersNew.dateRange.startDate || defaultFilters.dateRange?.startDate,
        filtersNew.dateRange.endDate || defaultFilters.dateRange?.endDate,
        groupPeriod
      );

      const requestOperation =
        filtersNew.operation === 'investment' && hasClientSideInvestmentRate
          ? 'git_effort'
          : filtersNew.operation;

      const responseData = await fetchData(
        {
          category: category,
          period: groupPeriod,
          ...filtersNew,
          operation: requestOperation
        },
        null,
        serviceURL
      );

      if (responseData) {
        const responseWithOperation =
          filtersNew.operation === 'investment' && hasClientSideInvestmentRate
            ? {
                ...responseData,
                groupedDataByPeriod: convertNumericValues(
                  responseData.groupedDataByPeriod,
                  averageDeveloperCost
                ),
                total: convertNumericValues(
                  responseData.total,
                  averageDeveloperCost
                )
              }
            : responseData;

        setLoading(false);
        setDashboardData({ ...responseWithOperation });
      }
    } catch (error) {
      toast.error('Data not found');
      setLoading(false);
      setDashboardData({});
      console.error('_______error', 'Error sending POST request:', error);
    }
  };

  useEffect(() => {
    if (!Object.keys(filters)?.length || !companyUid) return;
    if (!Object.keys(periods)?.length) return;

    getData();
  }, [companyUid, filters, periods, averageDeveloperCost]);

  useEffect(() => {
    const fetchAverageDeveloperCost = async () => {
      try {
        const response = await sendRequest('/company-configurations', 'GET');
        const fallbackCost = getAverageDeveloperCostFromSource(response);

        if (fallbackCost > 0) {
          setFallbackAverageDeveloperCost(fallbackCost);
        }
      } catch (error) {
        console.error('Failed to load fallback average developer cost', error);
      }
    };

    if (
      roleBasedAverageDeveloperCost > 0 ||
      !shouldFetchAverageDeveloperCostFromEndpoint(
        userInfo?.role,
        userInfo?.user?.role
      )
    ) {
      return;
    }

    fetchAverageDeveloperCost();
  }, [roleBasedAverageDeveloperCost, userInfo?.role, userInfo?.user?.role]);

  useEffect(() => {
    if (defaultFilters?.dateRange?.startDate) {
      setFilters(defaultFilters);
    }
  }, [
    defaultFilters?.dateRange?.startDate,
    defaultFilters?.dateRange?.endDate,
    defaultFilters?.operation,
    defaultFilters?.calculationsMethod
  ]);

  const handleFilters = filtersTemp => {
    setFilters(filtersTemp);
    if (dashboardFilters) {
      dashboardFilters(filtersTemp);
    }
  };

  const changePeriod = (elementId, period) => {
    setPeriods({ ...periods, [elementId]: period });
  };

  useEffect(() => {
    const allCategory = categoryArray;

    if (!allCategory) return;

    let filteredData = allCategory;

    if (searchTable.length > 0) {
      filteredData = Object.fromEntries(
        Object.entries(filteredData).filter(([, obj]) =>
          obj.label.toLowerCase().includes(searchTable.toLowerCase())
        )
      );
    }

    if (checkboxFromTable.length > 0) {
      filteredData = Object.fromEntries(
        Object.entries(filteredData).filter(([, obj]) =>
          checkboxFromTable.includes(obj.value)
        )
      );
    }

    setCategoryArrayFilter(filteredData);
  }, [categoryArray, searchTable, checkboxFromTable]);

  return (
    <>
      {filtersShow && (
        <div className="flex flex-wrap gap-3">
          <Filters
            userInfo={userInfo}
            onChange={handleFilters}
            isHidden={isHidden}
            isVisibleLangugageTags={isVisibleLangugageTags}
            operationKeys={operationKeys}
            showContextSummary={true}
            summaryCategory={category}
            forceClearMultiFilters={forceClearMultiFilters}
          />
        </div>
      )}
      {children}
      <div className="mt-4 space-y-4 md:mt-6 2xl:mt-7.5">
        {/* SplineChart ve PieChart */}
        <div className="flex min-w-0 flex-col gap-4 xl:flex-row">
          {splineChart && (
            <div
              className={`min-w-0 ${pieChart ? 'w-full xl:w-3/4' : 'w-full'}`}
            >
              <SplineChart
                loading={loading}
                dateRange={filters?.dateRange}
                categories={categoryArrayFilter}
                data={dashboardData?.groupedDataByPeriod}
                chartTitle={title.chartTitle}
                isTotal={false}
                sortingColumnName="total"
                sendCategoriesSend={e => {
                  setCategoriesOrder(e);
                }}
                changePeriod={e => changePeriod('chart1', e)}
                chartVariant={chartVariant}
              />
            </div>
          )}
          {/* PieChart */}
          {pieChart && (
            <div className="min-w-0 w-full xl:w-1/4">
              <PieChart
                loading={loading}
                categories={categoryArrayFilter}
                categoriesOrder={categoriesOrder}
                dateRange={filters?.dateRange}
                chartTitle={title.pieTitle}
                data={dashboardData?.total}
              />
            </div>
          )}
        </div>

        {/* Table */}
        <div>
          <Table
            loading={loading}
            type="dateGroup"
            formatNumberCell={true}
            firstColumnName={title?.tableColumnName}
            tableData={dashboardData?.groupedDataByPeriod}
            dataKey={categoryArray}
            linkKey={category}
            checkbox={showCheckbox}
            sorting={[{ id: 'total', desc: true }]}
            period={periods?.chart1}
            dateRange={filters?.dateRange}
            searchFromTable={setSearchTable}
            checkboxSelectFromTable={setCheckboxFromTable}
            isCheckContributionHeatmap={isCheckContributionHeatmap}
            showContributionTiers={filters?.operation === 'git_effort'}
          />
        </div>
      </div>
    </>
  );
};

export default DashboardWidgetNew;
